import type { IMessage, IFeedback, t_feedback_type } from '../types/network';
import type { KnowledgeBot } from '../core/knowledge_bot';

import { MessagingFacade } from './messaging_facade';

/**
 * store messages send from a network to an botInterface
 */
export class FeedbackFacade {
  /**
   * receive the list of messages from the KnowledgeBot
   */
  constructor(private messaging: MessagingFacade, private __messages: IMessage[], private bot: KnowledgeBot) {}

  /**
   * When user given feedback, update the message with the feedback
   */
  updateMessage(index: number, feedback: boolean) {
    this.messaging.updateMessages(index, { key: 'feedback', value: feedback });
    this.additionalComments(index, !feedback);
  }

  /**
   * send feedback to the bot.
   *
   * - if we request to send the same feedback then we send null
   * - if we have not sent any feedback or different feedback then we send the new feedback setting
   */
  async sendFeedback(feedbackType: t_feedback_type, token: string, messageIndex: number) {
    const hasDuplicates = this.hasFeedbackDuplicates(messageIndex, feedbackType);
    const currentFeedback = this.readFeedback(messageIndex);
    const feedback = hasDuplicates || feedbackType === currentFeedback ? null : feedbackType === 'like';

    // wait for the feedback to post before sending feedback
    //
    await this.postUserFeedback({ feedback, token }, messageIndex);
    return hasDuplicates;
  }

  /**
   * post feedback to the bot
   */
  postUserFeedback(data: IFeedback, messageIndex: number) {
    const { feedback, token, text = '' } = data;
    const feedbackPayload: IFeedback = {
      feedback,
      token,
      text
    };

    this.updateMessage(messageIndex, feedback as boolean);
    return this.bot.sendFeedback({ ...feedbackPayload });
  }

  /**
   * @returns
   */
  additionalSurvey(id: number, value: boolean) {
    return this.additionalComments(id, value);
  }

  /**
   * When user provided feedback comments, update the message with the feedback comments
   */
  additionalComments(index: number, value: boolean) {
    this.messaging.updateMessages(index, { key: 'feedbackComments', value });
  }

  /**
   * @returns true if the feedback is the same as what exists for the message id
   */
  hasFeedbackDuplicates(messageId: number, feedback: t_feedback_type) {
    return (
      'feedback' in this.__messages[messageId] &&
      this.__messages[messageId].feedback !== null &&
      this.__messages[messageId].feedback === (feedback === 'like')
    );
  }

  readFeedback(messageId: number) {
    return this.__messages[messageId].feedback;
  }
}
